import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getSettings } from "@/services/settingsService";

const TITLES: Record<string, string> = {
  terms: "Términos de Servicio",
  privacy: "Política de Privacidad",
};

const KEYS: Record<string, string> = {
  terms: "legal_tos",
  privacy: "legal_privacy",
};

function LegalHeader() {
  return (
    <header className="border-b bg-card px-6 py-4">
      <div className="max-w-3xl mx-auto flex items-center justify-between">
        <span className="font-semibold text-foreground">Senda de Vida</span>
        <nav className="flex gap-4 text-sm">
          <Link to="/legal/terms" className="text-muted-foreground hover:text-foreground">Términos</Link>
          <Link to="/legal/privacy" className="text-muted-foreground hover:text-foreground">Privacidad</Link>
        </nav>
      </div>
    </header>
  );
}

function LegalFooter() {
  return (
    <footer className="border-t px-6 py-4 text-center text-xs text-muted-foreground">
      &copy; {new Date().getFullYear()} Senda de Vida. Todos los derechos reservados.
    </footer>
  );
}

/** Página índice: /legal  (sin tipo) */
export function LegalIndexPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LegalHeader />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-12">
        <h1 className="text-3xl font-heading font-bold mb-2">Legal y Soporte</h1>
        <p className="text-muted-foreground mb-10">
          Aquí encontrarás nuestra documentación legal y opciones de contacto.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Link
            to="/legal/terms"
            className="block rounded-xl border bg-card p-6 hover:border-primary transition-colors"
          >
            <div className="text-2xl mb-2">📄</div>
            <h2 className="font-semibold text-lg mb-1">Términos de Servicio</h2>
            <p className="text-sm text-muted-foreground">
              Lee las condiciones de uso de la aplicación Senda de Vida.
            </p>
          </Link>

          <Link
            to="/legal/privacy"
            className="block rounded-xl border bg-card p-6 hover:border-primary transition-colors"
          >
            <div className="text-2xl mb-2">🔒</div>
            <h2 className="font-semibold text-lg mb-1">Política de Privacidad</h2>
            <p className="text-sm text-muted-foreground">
              Cómo recopilamos, usamos y protegemos tu información personal.
            </p>
          </Link>
        </div>

        <div className="mt-10 rounded-xl border bg-card p-6">
          <div className="text-2xl mb-2">✉️</div>
          <h2 className="font-semibold text-lg mb-1">Soporte</h2>
          <p className="text-sm text-muted-foreground mb-3">
            ¿Tienes alguna pregunta o problema con la app? Contáctanos y te ayudaremos.
          </p>
          <a
            href="mailto:soporte@sendadevida.com"
            className="inline-block text-sm text-primary font-medium hover:underline"
          >
            soporte@sendadevida.com
          </a>
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}

/** Página de documento específico: /legal/terms  o  /legal/privacy */
export default function LegalPage() {
  const { type } = useParams<{ type: string }>();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  const title = TITLES[type || ""] || "Documento Legal";
  const key = KEYS[type || ""];

  useEffect(() => {
    if (!key) {
      setLoading(false);
      return;
    }
    getSettings([key]).then((data) => {
      setContent(data[key] || "");
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [key]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LegalHeader />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-10">
        <Link to="/legal" className="text-sm text-primary hover:underline mb-6 inline-block">&larr; Legal y Soporte</Link>
        <h1 className="text-3xl font-heading font-bold mb-6">{title}</h1>
        {loading ? (
          <p className="text-muted-foreground">Cargando...</p>
        ) : content ? (
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-foreground leading-relaxed">
            {content}
          </div>
        ) : (
          <p className="text-muted-foreground">Este documento aún no ha sido configurado.</p>
        )}
      </main>
      <LegalFooter />
    </div>
  );
}
